// src/components/MissedCheckoutAlert.js
//
// Dashboard banner shown to an employee/trainee who was counted absent for a day because they
// checked in but never checked out (marked by the markMissedCheckoutsAbsent Cloud Function, or by
// the MissedCheckoutModal). It appears for 24h after the absence was recorded and lets the person
// submit a departure-time request to the manager so the day can be counted as worked instead.
//
// Detection is index-free: attendance doc ids are `${uid}_${YYYY-MM-DD}`, so we read the last few
// days directly by id rather than running a query that would need a composite index.

import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiAlertTriangle, FiClock, FiCheckCircle } from 'react-icons/fi';

const TZ = 'Asia/Jerusalem';
const DAY_MS = 24 * 60 * 60 * 1000;

// YYYY-MM-DD for a Date in the office timezone.
const dateStrTZ = (date) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${m.year}-${m.month}-${m.day}`;
};

// Arabic weekday + Gregorian (not Hijri) date, e.g. "الأحد، 20 يوليو 2026".
const formatArabicGregorian = (dateStr) =>
  new Date(dateStr + 'T12:00:00').toLocaleDateString('ar', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', calendar: 'gregory',
  });

export default function MissedCheckoutAlert() {
  const { currentUser, userRole } = useAuth();

  const [record, setRecord] = useState(null); // { docId, date, checkInTime, reconsiderRequested }
  const [timeInput, setTimeInput] = useState('17:00');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) return;
    let cancelled = false;

    const findRecentAbsence = async () => {
      try {
        // Check the last 3 days (covers a weekend gap between the absence and the person logging in).
        const candidates = [];
        for (let i = 1; i <= 3; i += 1) {
          const dateStr = dateStrTZ(new Date(Date.now() - i * DAY_MS));
          const ref = doc(db, 'attendance', `${currentUser.uid}_${dateStr}`);
          // eslint-disable-next-line no-await-in-loop
          const snap = await getDoc(ref);
          if (!snap.exists()) continue;
          const data = snap.data();
          const markedAt = data.absentMarkedAt?.toDate ? data.absentMarkedAt.toDate() : null;
          if (
            data.absentReason === 'no_checkout' &&
            markedAt &&
            Date.now() - markedAt.getTime() < DAY_MS
          ) {
            candidates.push({ docId: snap.id, ...data });
          }
        }
        if (cancelled) return;
        candidates.sort((a, b) => b.date.localeCompare(a.date));
        setRecord(candidates[0] || null);
      } catch (err) {
        console.error('[MissedCheckoutAlert] detection failed:', err);
      }
    };

    findRecentAbsence();
    return () => { cancelled = true; };
  }, [currentUser]);

  const handleSubmit = useCallback(async () => {
    if (!record || !currentUser?.uid) return;
    setError('');

    const [h, m] = timeInput.split(':').map(Number);
    const [y, mo, d] = record.date.split('-').map(Number);
    const departure = new Date(y, mo - 1, d, h, m, 0, 0);
    const checkInDate = record.checkInTime?.toDate ? record.checkInTime.toDate() : null;
    if (checkInDate && departure <= checkInDate) {
      setError('وقت المغادرة يجب أن يكون بعد وقت الدخول.');
      return;
    }

    setBusy(true);
    try {
      const displayDate = formatArabicGregorian(record.date);
      await addDoc(collection(db, 'workHoursRequests'), {
        type: 'hours',
        status: 'Pending',
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email || currentUser.uid,
        userEmail: currentUser.email || '',
        userRole: userRole || '',
        date: record.date,
        attendanceId: record.docId,
        proposed: {
          checkInTime: record.checkInTime || null,
          checkOutTime: Timestamp.fromDate(departure),
        },
        details: `طلب احتساب دوام ليوم ${displayDate} — نسيت تسجيل الخروج. وقت المغادرة: ${timeInput}`,
        reason: 'missed_checkout',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Flag the attendance day so we don't offer the form again (index-free "already requested").
      await updateDoc(doc(db, 'attendance', record.docId), {
        reconsiderRequested: true,
        reconsiderRequestedAt: serverTimestamp(),
      });

      setSubmitted(true);
    } catch (err) {
      console.error('[MissedCheckoutAlert] submit failed:', err);
      setError('حدث خطأ أثناء إرسال الطلب، حاول مجدداً.');
    } finally {
      setBusy(false);
    }
  }, [record, currentUser, userRole, timeInput]);

  if (!record) return null;

  const displayDate = formatArabicGregorian(record.date);
  const checkInStr = record.checkInTime?.toDate
    ? record.checkInTime.toDate().toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' })
    : '—';
  const alreadyRequested = submitted || record.reconsiderRequested;

  const wrap = {
    direction: 'rtl',
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: 14,
    padding: '16px 18px',
    marginBottom: 18,
    fontFamily: 'Nunito, Segoe UI, sans-serif',
  };

  return (
    <div style={wrap}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <FiAlertTriangle style={{ color: '#ea580c', fontSize: 20, flexShrink: 0 }} />
        <div style={{ fontWeight: 900, color: '#9a3412', fontSize: 15 }}>
          تم احتسابك غائبًا عن الدوام
        </div>
      </div>

      <p style={{ margin: '0 0 10px', color: '#7c2d12', fontSize: 14, lineHeight: 1.8 }}>
        يوم <strong>{displayDate}</strong> — سجّلت دخولك الساعة <strong>{checkInStr}</strong> ولم
        تُسجّل خروجك، لذلك لم يُحتسب اليوم كدوام.
      </p>

      {alreadyRequested ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46',
          borderRadius: 10, padding: '10px 14px', fontSize: 14, fontWeight: 700,
        }}>
          <FiCheckCircle />
          تم إرسال طلبك للمدير — بانتظار الموافقة على احتساب دوامك.
        </div>
      ) : (
        <>
          <p style={{ margin: '0 0 10px', color: '#7c2d12', fontSize: 13.5, lineHeight: 1.7 }}>
            قدّم طلبًا للمدير بساعة مغادرتك الفعلية حتى يتم احتساب اليوم كدوام بدل غياب:
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiClock style={{ color: '#ea580c' }} />
              <input
                type="time"
                value={timeInput}
                onChange={(e) => { setError(''); setTimeInput(e.target.value); }}
                disabled={busy}
                style={{
                  padding: '9px 12px', borderRadius: 10, border: '1.5px solid #fdba74',
                  fontSize: 16, fontWeight: 700, textAlign: 'center', color: '#1e293b',
                  fontFamily: 'inherit', outline: 'none',
                }}
              />
            </div>
            <button
              onClick={handleSubmit}
              disabled={busy}
              style={{
                padding: '10px 18px', background: '#ea580c', color: '#fff', border: 'none',
                borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: busy ? 'not-allowed' : 'pointer',
                opacity: busy ? 0.65 : 1, fontFamily: 'inherit',
              }}
            >
              {busy ? 'جاري الإرسال...' : 'إرسال الطلب للمدير'}
            </button>
          </div>
          {error && (
            <div style={{ marginTop: 10, color: '#dc2626', fontSize: 13, fontWeight: 700 }}>{error}</div>
          )}
        </>
      )}
    </div>
  );
}
